import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  useTheme,
  MenuItem,
  TextField,
  IconButton,
} from "@mui/material";
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import { Delete, Edit } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import {
  EventData,
  EventDataDetails,
} from "../../interfaces/planner.interface";
import {
  updateEventDetails,
  createEventVenueDetail,
  deleteVenueDetail,
  updateVenueDetail,
  updateBanner,
  updateBannerAlt,
} from "../../api/providers/plannerEventsProviders";

import CustomEditModal from "../public/CustomEditModal";
import CustomWarningModal from "../public/CustomWarningModal";

interface PlannerEventDetailsProps {
  eventData: EventDataDetails;
  eventId: number;
  loading: boolean;
  selectedEventData: () => void;
  setEventData: React.Dispatch<React.SetStateAction<EventData | undefined>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

interface CurrentVenueDetail {
  detail: string;
  indicator: boolean;
}

const mapContainerStyle = {
  width: "100%",
  height: "250px",
};

const defaultCenter = {
  lat: 9.953158,
  lng: -84.086716,
};

const formattedDate = (dateString: string | undefined) => {
  const date = new Date(dateString);
  const options = {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };
  const formatted = date.toLocaleDateString("es-ES", options);
  return formatted.replace("a.m.", "AM").replace("p.m.", "PM");
};

export const PlannerEventDetails: React.FC<PlannerEventDetailsProps> = ({
  eventData,
  eventId,
  selectedEventData,
  loading,
  setEventData,
  setLoading,
}) => {
  const [showEditDetails, setShowEditDetails] = useState(false);
  const [name, setName] = useState<string | undefined>(eventData?.name);
  const [category, setCategory] = useState<string | undefined>(
    eventData?.category?.name
  );
  const [address, setAddress] = useState<string | undefined>(
    eventData?.address
  );
  const [startDate, setStartDate] = useState<string | undefined>("");
  const [endDate, setEndDate] = useState<string | undefined>("");
  const [description, setDescription] = useState<string | undefined>(
    eventData?.description
  );
  const [showCreateDetail, setShowCreateDetail] = useState(false);
  const [venueDetail, setVenueDetail] = useState();
  const [selectedVenueDetail, setSelectedVenueDetail] =
    useState<CurrentVenueDetail | null>(null);
  const [selectedVenueId, setselectedVenueId] = useState<number>();
  const [venueIndicator, setVenueIndicator] = useState(true);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const theme = useTheme();

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_MAPS_KEY || "",
  });

  const updateDetails = async () => {
    try {
      setLoading(true);
      const result = await updateEventDetails(
        {
          name,
          category,
          address,
          startDate,
          endDate,
          description,
        },
        eventId
      );
      if (result.ok) {
        setEventData(result.data);
        setShowEditDetails(false);
      }
    } catch (error) {
      console.error("Error obteniendo los datos del usuario:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    updateFunction: (
      id: number | undefined,
      formData: FormData
    ) => Promise<{ ok: boolean; data?: any; errorMessage?: string }>,
    eventId: number | undefined
  ) => {
    const file = event.target.files?.[0];

    if (!file || eventId) {
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await updateFunction(eventId, formData);
      if (response.ok) {
        selectedEventData();
      } else {
        console.error("Error al subir la imagen:", response.errorMessage);
      }
    } catch (error) {
      console.error("Error inesperado al subir la imagen", error);
    } finally {
      setLoading(false);
    }
  };

  const createVenueDetail = async () => {
    try {
      setLoading(true);
      const result = await createEventVenueDetail({
        eventId,
        detail: venueDetail,
        indicator: venueIndicator,
      });
      if (result.ok) {
        selectedEventData();
        setShowCreateDetail(false);
      }
    } catch (error) {
      console.error("Error obteniendo los datos del usuario:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVenueDetail = async () => {
    try {
      setLoading(true);
      const result = await deleteVenueDetail(selectedVenueId);
      if (result.ok) {
        selectedEventData();
        setShowWarningModal(false);
      }
    } catch (error) {
      console.error("Error obteniendo los datos del usuario:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateVenueDetail = async () => {
    try {
      setLoading(true);
      const result = await updateVenueDetail(
        selectedVenueId,
        selectedVenueDetail
      );
      if (result.ok) {
        selectedEventData();
        setShowWarningModal(false);
      }
    } catch (error) {
      console.error("Error obteniendo los datos del usuario:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id: number) => {
    setselectedVenueId(id);
    setShowWarningModal(true);
  };

  const handleEditClick = (id: number, detail: string, indicator: boolean) => {
    setselectedVenueId(id);
    setSelectedVenueDetail({ detail, indicator });
    setShowEditModal(true);
  };

  const columns: GridColDef[] = [
    { field: "detail", headerName: "Detalle", width: 350 },
    {
      field: "indicator",
      headerName: "Indicador",
      width: 120,
      type: "boolean",
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 140,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <>
          <IconButton
            onClick={() =>
              handleEditClick(
                +params.id,
                params.row.detail,
                params.row.indicator
              )
            }
          >
            <Edit />
          </IconButton>

          <IconButton onClick={() => handleDeleteClick(+params.id)}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  const rows: GridRowsProp = eventData?.venueDetails || [];

  const handleButton = () => {
    if (showEditDetails) return updateDetails();
    return setShowEditDetails(true);
  };

  const buttonText = () => {
    if (showEditDetails) return "Guardar";
    return "Editar Detalles";
  };

  const handleStartDateChange = (newValue: any) => {
    if (newValue) {
      const formattedDate = dayjs(newValue).format("YYYY-MM-DD");
      setStartDate(formattedDate);
    } else {
      setStartDate("");
    }
  };

  const handleEndDateChange = (newValue: any) => {
    if (newValue) {
      const formattedDate = dayjs(newValue).format("YYYY-MM-DD");
      setEndDate(formattedDate);
    } else {
      setEndDate("");
    }
  };

  const handleCancelButton = () => {
    return setShowCreateDetail(false);
  };

  const handleStateChange = (event: any) => {
    setVenueIndicator(event.target.value === "true");
  };

  const handleEditIndicator = (event: any) => {
    if (selectedVenueDetail) {
      setSelectedVenueDetail({
        ...selectedVenueDetail,
        indicator: event.target.value === "true",
      });
    }
  };

  const handleEditDetail = (event: any) => {
    if (selectedVenueDetail) {
      setSelectedVenueDetail({
        ...selectedVenueDetail,
        detail: event.target.value,
      });
    }
  };

  const handleCloseWarningModal = () => {
    return setShowWarningModal(false);
  };

  const handleCloseEditModal = () => {
    return setShowEditModal(false);
  };
  const handleMapClick = async (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();

      setEventData((prevData) => ({
        ...prevData,
        latitude: lat,
        longitude: lng,
      }));

      const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${import.meta.env.VITE_MAPS_KEY
        }`;

      try {
        const response = await fetch(geocodeUrl);
        const data = await response.json();
        if (data.results && data.results.length > 0) {
          const address = data.results[0].formatted_address;

          setEventData((prevData) => ({
            ...prevData,
            address: address ?? "",
          }));
        } else {
          console.error("No address found for the given coordinates.");
          setEventData((prevData) => ({
            ...prevData,
            address: "",
          }));
        }
      } catch (error) {
        console.error("Error fetching address data:", error);
        setEventData((prevData) => ({
          ...prevData,
          address: "",
        }));
      }
    }
  };

  const mapCenter =
    eventData?.latitude && eventData?.longitude
      ? { lat: eventData?.latitude, lng: eventData?.longitude }
      : defaultCenter;

  if (!isLoaded) return null;

  return (
    <Grid>
      <Grid container spacing={2} pt={2} mt={1} alignItems="flex-start">
        <Grid item xs={12} >
          <Typography
            variant="h6"
            color="textPrimary"
            fontSize={{ xs: 24, md: 24 }}
            sx={{ fontWeight: "bold" }}
          >
            Imagenes del evento
          </Typography>
        </Grid>
        <Grid item xs={12} sm={12} md={4} position="relative">
          <Box
            sx={{
              width: "100%",
              height: 266,
              backgroundColor: eventData?.banner ? "transparent" : "grey",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 2,
              position: "relative",
              overflow: "hidden",
            }}
          >
            {!eventData?.banner && (
              <Typography color="white" variant="subtitle1">
                Imagen principal
              </Typography>
            )}
            {eventData?.banner && (
              <img
                src={eventData?.banner}
                alt="Imagen principal"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}
            <Button
              variant="contained"
              component="label"
              sx={{
                position: "absolute",
                top: 30,
                right: 10,
                backgroundColor: "lightgreen",
                borderRadius: "10%",
              }}
              disabled={loading}
            >
              <Edit />
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) =>
                  handleFileChange(e, updateBanner, eventData?.id)
                }
              />
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} sm={12} md={8} position="relative">
          <Box
            sx={{
              width: "100%",
              height: 266,
              backgroundColor: eventData?.bannerAlt ? "transparent" : "grey",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: 2,
              position: "relative",
              overflow: "hidden",
            }}
          >
            {!eventData?.bannerAlt && (
              <Typography color="white" variant="subtitle1">
                Banner
              </Typography>
            )}
            {eventData?.bannerAlt && (
              <img
                src={eventData.bannerAlt}
                alt="Banner"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}
            <Button
              variant="contained"
              component="label"
              sx={{
                position: "absolute",
                top: 30,
                right: 10,
                backgroundColor: "lightgreen",
                borderRadius: "10%",
              }}
              disabled={loading}
            >
              <Edit />
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) =>
                  handleFileChange(e, updateBannerAlt, eventData.id)
                }
              />
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={2} pt={2} mt={4} mb={2} alignItems="flex-start">
        <Grid item xs={12} md={6}>
          <Typography
            variant="h6"
            color="textPrimary"
            fontSize={{ xs: 24, md: 24 }}
            sx={{ fontWeight: "bold" }}

          >
            Detalles del evento
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography
            variant="h6"
            color="textPrimary"
            fontSize={{ xs: 24, md: 24 }}
            sx={{ fontWeight: "bold" }}

          >
            Lugar del evento
          </Typography>
        </Grid>
      </Grid>

      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>

              <TextField
                label="Nombre del evento"
                variant={showEditDetails ? "filled" : "outlined"}
                fullWidth
                color="primary"
                value={name}
                onChange={(e) => setName(e.target.value)}
                InputProps={{
                  readOnly: !showEditDetails,
                }}
              />
            </Grid>
            <Grid item xs={12}>

              <TextField
                label="Categoría"
                variant={showEditDetails ? "filled" : "outlined"}
                fullWidth
                color="primary"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                InputProps={{
                  readOnly: !showEditDetails,
                }}
              />
            </Grid>
            <Grid item xs={6}>
           
              {showEditDetails ? (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={startDate ? dayjs(startDate) : null}
                    onChange={handleStartDateChange}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth variant="outlined" />
                    )}
                  />
                </LocalizationProvider>
              ) : (
                <TextField
                 label="Fecha y hora de inicio"
                  variant="outlined"
                  fullWidth

                  value={formattedDate(eventData?.startDate)}
                />
              )}
            </Grid>
            <Grid item xs={6}>
             
              {showEditDetails ? (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={endDate ? dayjs(endDate) : null}
                    onChange={handleEndDateChange}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth variant="outlined" />
                    )}
                  />
                </LocalizationProvider>
              ) : (
                <TextField
                  variant="outlined"
                  fullWidth

                  value={formattedDate(eventData?.endDate)}
                />
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="Ubicacion en el mapa"
            variant={showEditDetails ? "filled" : "outlined"}
            fullWidth
            color="primary"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            InputProps={{
              readOnly: !showEditDetails,
            }}
          />
          <Box sx={{ marginTop: 2 }}>
            <GoogleMap

              mapContainerStyle={{
                ...mapContainerStyle,
                borderRadius: '16px',
              }}
              zoom={15}
              center={mapCenter}
              onClick={handleMapClick}

            >
              <Marker
                position={{
                  lat: eventData?.latitude,
                  lng: eventData?.longitude,
                }}
              />
            </GoogleMap>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            color="textPrimary"
            fontSize={{ xs: 24, md: 24 }}
            sx={{ fontWeight: "bold", mb: 2 }}
          >
            Descripción del evento
          </Typography>
          <TextField
            variant={showEditDetails ? "filled" : "outlined"}
            multiline
            fullWidth
            color="primary"
            value={description}
            sx={{ mb: 2 }}
            onChange={(e) => setDescription(e.target.value)}
            InputProps={{
              readOnly: !showEditDetails,
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 5 }}>
            <Button
              variant="contained"
              sx={{
                bgcolor: "primary.light",
                borderRadius: 1,
                width: 200,
              }}
              onClick={handleButton}
            >
              {buttonText()}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            color="textPrimary"
            fontSize={{ xs: 24, md: 24 }}
            sx={{ fontWeight: "bold", mb: 2 }}
          >
            Detalles del lugar
          </Typography>
          {eventData && eventData.venueDetails.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <DataGrid
                rows={rows}
                columns={columns}
                sx={{ padding: "10px" }}
              />
            </Box>
          )}
          <Grid container alignItems="center">
            {showCreateDetail ? (
              <Grid item xs={12}>
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={3}>
                    <TextField
                      label="Detalle"
                      fullWidth
                      variant="outlined"
                      value={venueDetail}
                      onChange={(e) => setVenueDetail(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      select
                      fullWidth
                      label="Indicador"
                      variant="outlined"
                      value={venueIndicator.toString()}
                      onChange={handleStateChange}
                    >
                      <MenuItem value="true">Positivo</MenuItem>
                      <MenuItem value="false">Negativo</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mb: 5 }}
                >
                  <Button
                    variant="contained"
                    sx={{ bgcolor: "primary.light", borderRadius: 1, mr: 2 }}
                    onClick={createVenueDetail}
                  >
                    Guardar
                  </Button>
                  <Button
                    variant="contained"
                    sx={{ bgcolor: "secondaryAccent.dark", borderRadius: 1 }}
                    onClick={handleCancelButton}
                  >
                    Cancelar
                  </Button>
                </Box>
              </Grid>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "100%",
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "primary.light",
                    borderRadius: 1,
                    width: 200,
                  }}
                  onClick={() => setShowCreateDetail(true)}
                >
                  Agregar nuevo
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Grid>

      <CustomWarningModal
        onPressCancel={handleCloseWarningModal}
        isVisible={showWarningModal}
        onPressConfirm={handleDeleteVenueDetail}
        title="Estás seguro de eliminar el detalle?"
      />

      <CustomEditModal
        isVisible={showEditModal}
        onPressConfirm={handleUpdateVenueDetail}
        onPressCancel={handleCloseEditModal}
      >
        <Grid container spacing={2} mb={2}>
          <Grid item xs={6}>
            <TextField
              label="Detalle"
              fullWidth
              variant="outlined"
              value={venueDetail?.detail}
              onChange={handleEditDetail}
            />
          </Grid>
          <Grid item xs={5}>
            <TextField
              select
              fullWidth
              label="Indicador"
              variant="outlined"
              value={venueDetail?.indicator.toString()}
              onChange={handleEditIndicator}
            >
              <MenuItem value="true">Positivo</MenuItem>
              <MenuItem value="false">Negativo</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </CustomEditModal>
    </Grid>
  );
};

